// mindmetal-app/web/src/components/employee/ServiceStatusCard.tsx
import { motion } from 'framer-motion'
import { Card, CardContent } from '@personic/shared'

export type ServiceStatus = 'healthy' | 'degraded' | 'down' | 'unknown'

interface ServiceStatusCardProps {
  name: string
  status: ServiceStatus
  uptime?: number
  lastCheck?: string
  responseTime?: number
  errorCount?: number
}

const statusConfig: Record<ServiceStatus, { color: string; bgColor: string; label: string }> = {
  healthy: { color: 'bg-emerald-500', bgColor: 'bg-emerald-50', label: 'Healthy' },
  degraded: { color: 'bg-amber-500', bgColor: 'bg-amber-50', label: 'Degraded' },
  down: { color: 'bg-red-500', bgColor: 'bg-red-50', label: 'Down' },
  unknown: { color: 'bg-gray-400', bgColor: 'bg-gray-50', label: 'Unknown' },
}

export function ServiceStatusCard({
  name,
  status,
  uptime,
  lastCheck,
  responseTime,
  errorCount,
}: ServiceStatusCardProps) {
  const config = statusConfig[status]

  return (
    <Card variant="outlined" padding="none">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <motion.div
              className={`w-3 h-3 rounded-full ${config.color}`}
              animate={status === 'healthy' ? {} : { scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 2 }}
            />
            <div>
              <h4 className="font-medium text-midnight-800">{name}</h4>
              <span className={`text-xs px-2 py-0.5 rounded-full ${config.bgColor} text-midnight-700`}>
                {config.label}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          {uptime !== undefined && (
            <div>
              <span className="text-warm-500 block text-xs">Uptime</span>
              <span className="font-mono text-midnight-800">{uptime.toFixed(2)}%</span>
            </div>
          )}
          {responseTime !== undefined && (
            <div>
              <span className="text-warm-500 block text-xs">Response Time</span>
              <span className="font-mono text-midnight-800">{responseTime}ms</span>
            </div>
          )}
          {errorCount !== undefined && (
            <div>
              <span className="text-warm-500 block text-xs">Errors (24h)</span>
              <span className={`font-mono ${errorCount > 0 ? 'text-red-600' : 'text-midnight-800'}`}>
                {errorCount}
              </span>
            </div>
          )}
          {lastCheck && (
            <div>
              <span className="text-warm-500 block text-xs">Last Check</span>
              <span className="font-mono text-midnight-800 text-xs">{lastCheck}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
